'use client';

import { useBranding } from '@test-prep/b2b-branding-client';

const FONT: React.CSSProperties = {
  fontFamily: 'Outfit, sans-serif',
  fontSize: 'clamp(2.5rem, 10vw, 4rem)',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  lineHeight: 1,
  userSelect: 'none',
};

// The wordmark spells the tenant's app name (default "Aksademia"). The first half
// renders white and the second half a darker teal accent — for "Aksademia" this is
// the original "Aksa" (white) / "demia" (dark) split. The background follows the
// tenant's --teal brand override; the dark accent stays a fixed legible teal so the
// letters never collapse into the background when a tenant overrides only --teal.
const LETTER_COLOR_WHITE = '#ffffff';
const LETTER_COLOR_DARK = '#134e4a';

export function LoadingScreen() {
  const { appName } = useBranding();
  const letters = Array.from(appName);
  const splitAt = Math.floor(letters.length / 2);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center"
      style={{ backgroundColor: 'var(--teal)' }}
    >
      {/* Brand pattern behind the wordmark. White at low alpha, so it follows a
          tenant's --teal override, and fainter than elsewhere so the wordmark
          stays the focus. Positioned inline because .bg-pattern sets
          position: relative, which would otherwise win over a utility class. */}
      <div
        aria-hidden="true"
        className="bg-pattern"
        style={{ position: 'absolute', inset: 0, '--pattern-color': 'rgb(255 255 255 / 0.04)' } as React.CSSProperties}
      />
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'baseline',
        }}
      >
        {letters.map((char, i) => (
          <span
            key={i}
            style={{
              ...FONT,
              color: i < splitAt ? LETTER_COLOR_WHITE : LETTER_COLOR_DARK,
              animation: 'letterSweep 2.2s ease-in-out infinite',
              animationDelay: `${i * 0.1}s`,
            }}
          >
            {char}
          </span>
        ))}
      </div>

      {/* Progress bar — pinned to bottom */}
      <div className="fixed bottom-0 left-0 right-0 h-[3px]" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
        <div style={{ height: '100%', backgroundColor: 'rgba(255,255,255,0.5)', animation: 'progressBar 2.2s ease-in-out infinite' }} />
      </div>

      <style>{`
        @keyframes letterSweep {
          0%, 15%  { opacity: 0.18; }
          45%, 60% { opacity: 1; }
          90%, 100% { opacity: 0.18; }
        }
        @keyframes progressBar {
          0%   { width: 0%; }
          80%  { width: 100%; }
          100% { width: 100%; opacity: 0; }
        }
      `}</style>
    </div>
  );
}
